import React, { useMemo } from 'react';
import fileDownload from 'js-file-download';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Divider,
  DownloadIcon,
  Grid,
  Paper,
  PrintIcon,
  Typography,
} from '@availity/element';

import { Footer, BackupCodeDisplay, AvailityHeaderLogo } from '@/components';
import { useFRCallback } from '@/hooks';

const TwoFAManageBackupCodes = ({ ...props }) => {
  const { stepToRender, setCurrentStep } = props;
  const { payload = {} } = stepToRender;
  const { stage } = payload;
  const formattedStage = useMemo(() => stage?.toLowerCase(), [stage]);

  const { callbacks, confirmationCallbackIndex, confirmationCallbackOptions, textOutputCallback } =
    useFRCallback(stepToRender);

  const backupCodes = useMemo(() => {
    let output = [];
    if (textOutputCallback) {
      const textOutputArray = find(
        textOutputCallback.output,
        (outputObj) => outputObj?.name?.toLowerCase() === 'message'
      );

      // NOTE: if no codes present, the value will be 'default message'; in this case return an empty array.
      const unparsedOutput = textOutputArray?.value?.toLowerCase() !== 'default message' && textOutputArray?.value;

      output = unparsedOutput ? JSON.parse(unparsedOutput) : [];
    }

    return output;
  }, [textOutputCallback]);

  const handleDownloadCodes = () => {
    const filename = 'av-backup-codes.txt';
    const prompt = 'NOTE: You can use each backup code only once. When you use one, cross it off the list.\n\n';
    const codes = backupCodes.join(' - ');

    const text = prompt.concat(codes);
    fileDownload(text, filename);
  };

  // handle other navigation
  const handleNavigation = (selectedNavigation) => {
    const formattedSelectedNavigation = selectedNavigation?.toLowerCase();
    const nextStep = { ...stepToRender };

    if (formattedSelectedNavigation) {
      const newCallbacks = [...callbacks];

      const navigationIndex = findIndex(
        confirmationCallbackOptions,
        (option) => option?.toLowerCase() === formattedSelectedNavigation
      );

      if (navigationIndex >= 0) {
        // set the confirmation callback input value to the removeButtonIndex
        newCallbacks[confirmationCallbackIndex].input[0] = {
          ...newCallbacks[confirmationCallbackIndex].input[0],
          value: navigationIndex,
        };
      }

      // set the new callbacks in the next step
      Object.assign(nextStep.payload.callbacks, newCallbacks);
    }

    // update the currentStep to trigger update
    setCurrentStep({ ...nextStep });
  };

  return (
    <Grid
      id="2fa-manage-backup-codes-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px', md: '600px' } }}>
            <CardContent>
              <Typography variant="h1" mb={1}>
                Security Settings
              </Typography>
              <Typography variant="h2" mb={2}>
                Backup Codes
              </Typography>
              <Divider sx={{ marginY: 2 }} />
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Keep these backup codes in a safe place only you know about.
                </Box>
              </Typography>
              <Typography variant="subtitle1">
                In the event that you don&apos;t have your phone or Authenticator app, use backup codes to access your
                account. Each backup code can be used only once.
              </Typography>
              <Grid display="flex" flexWrap="wrap" my={3}>
                <BackupCodeDisplay backupCodes={backupCodes} />
              </Grid>
              <Box mb={3}>
                <Button color="tertiary" startIcon={<PrintIcon />} onClick={() => window.print()}>
                  Print
                </Button>{' '}
                <Button color="tertiary" startIcon={<DownloadIcon />} onClick={handleDownloadCodes}>
                  Download
                </Button>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'end' }}>
              <Button color="secondary" onClick={() => handleNavigation('back')}>
                Back
              </Button>
              {formattedStage === 'mfamanage-showcodes' && (
                <Button color="primary" onClick={() => handleNavigation('new codes')}>
                  Generate New Backup Codes
                </Button>
              )}
            </CardActions>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

TwoFAManageBackupCodes.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default TwoFAManageBackupCodes;
